import { useState, useRef, useLayoutEffect, useCallback } from 'react'
import { Box } from '@mui/material'
import dayjs from 'dayjs'
import { CalIc, CAL_HOUR_H, calClosedRanges, calFmtT, calInitials, placeClean } from './calShared'
import { CalApptCard, CalClusterCard } from './CalendarApptCard'

/**
 * Desktop time grid per the Claude Design "Calendar · five views" +
 * "Overlap redesign · clustered columns".
 *
 * mode="days"  → one column per day (3 / 5 / week views); appointments from
 *                every displayed employee merge into the day column, and tight
 *                overlaps collapse into cluster cards.
 * mode="lanes" → single day, one lane per employee (Day view) with an
 *                avatar + count lane head.
 *
 * The 24-hour body scrolls under a sticky header; the merchant's closed time
 * (outside that weekday's open hours, or the whole day on closed dates) is
 * hatched; hovering empty grid shows the click-to-book ghost; today's column
 * carries the live now-line.
 */

const BORDER = '#E7E2F3'
const COL_LINE = '#F1EFF8'
const MUTED = '#8B85A4'
const TEXT = '#2E2A40'
const FONT = "'Outfit', sans-serif"
const GUTTER_W = 56
const GRID_H = 24 * CAL_HOUR_H

const HATCH = 'repeating-linear-gradient(-45deg, rgba(139, 133, 164, 0.07) 0 5px, transparent 5px 11px)'

const colBgSx = {
	position: 'relative',
	borderLeft: `1px solid ${COL_LINE}`,
	minWidth: 0,
	backgroundImage: `
		linear-gradient(to bottom, #EFECF6 1px, transparent 1px),
		linear-gradient(to bottom, transparent 42px, #F6F4FA 42px, #F6F4FA 43px, transparent 43px)
	`,
	backgroundSize: `100% ${CAL_HOUR_H}px, 100% ${CAL_HOUR_H}px`,
}

// snap mouse position to a 30-min slot; null when over a card/cluster
function slotFromEvent(e, el) {
	if (e.target.closest && e.target.closest('[data-calcard]')) return null
	const r = el.getBoundingClientRect()
	// account for page zoom (html { zoom: 0.9 } on desktop)
	const y = (e.clientY - r.top) * (GRID_H / r.height)
	const slot = Math.floor(y / (CAL_HOUR_H / 2)) * 30
	return slot >= 0 && slot + 30 <= 24 * 60 ? slot : null
}

// Hatches the day's closed time (outside the merchant's open hours for that
// weekday; the whole day on closed dates). Display only — pointer events pass
// through, so the merchant can still click-to-book on hatched time.
function OffHoursShading({ day, merchant }) {
	const { ranges } = calClosedRanges(day, merchant)
	return (
		<>
			{ranges.map((r, i) => (
				<Box key={i} sx={{
					position: 'absolute', left: 0, right: 0, zIndex: 1, pointerEvents: 'none',
					top: (r.from / 60) * CAL_HOUR_H, height: ((r.to - r.from) / 60) * CAL_HOUR_H,
					background: HATCH,
				}} />
			))}
		</>
	)
}

function NowLine({ now }) {
	const mins = now.hour() * 60 + now.minute()
	return (
		<Box sx={{
			position: 'absolute', left: 0, right: 0, top: (mins / 60) * CAL_HOUR_H,
			borderTop: '2px solid #E5484D', zIndex: 30, pointerEvents: 'none',
			'&::before': {
				content: '""', position: 'absolute', left: '-3px', top: '-5px', width: 8, height: 8,
				borderRadius: '50%', bgcolor: '#E5484D', boxShadow: '0 0 0 2px #FFFFFF',
			},
		}} />
	)
}

function Ghost({ mins }) {
	return (
		<Box sx={{
			position: 'absolute', left: 4, right: 4, zIndex: 4, top: (mins / 60) * CAL_HOUR_H,
			height: CAL_HOUR_H / 2 - 3, border: '1.5px dashed #A99BE8',
			bgcolor: 'rgba(124, 107, 217, 0.06)', borderRadius: '8px', display: 'flex',
			alignItems: 'center', gap: '6px', px: '9px', color: '#5F4EC2', fontSize: '11px',
			fontWeight: 750, pointerEvents: 'none', fontFamily: FONT,
		}}>
			<CalIc name="plus" size={12} sw={2.2} />
			<span>{calFmtT(dayjs().startOf('day').add(mins, 'minute'), true)}</span>
		</Box>
	)
}

function Gutter({ now, showNow }) {
	const hours = Array.from({ length: 24 }, (_, h) => h)
	const nowMins = now.hour() * 60 + now.minute()
	return (
		<Box sx={{ position: 'relative' }}>
			{hours.map(h => (
				h === 0 ? null : (
					<Box key={h} component="span" sx={{
						position: 'absolute', right: 8, top: h * CAL_HOUR_H, transform: 'translateY(-50%)',
						fontSize: '10.5px', fontWeight: 650, color: '#A49DBD', whiteSpace: 'nowrap',
					}}>
						{calFmtT(dayjs().startOf('day').hour(h), true)}
					</Box>
				)
			))}
			{showNow && (
				<Box component="span" sx={{
					position: 'absolute', right: 5, top: (nowMins / 60) * CAL_HOUR_H, transform: 'translateY(-50%)',
					bgcolor: '#E5484D', color: '#FFFFFF', fontSize: '9.5px', fontWeight: 800,
					px: '6px', py: '2.5px', borderRadius: '6px', zIndex: 31, letterSpacing: '0.02em',
					whiteSpace: 'nowrap',
				}}>
					{calFmtT(now, false)}
				</Box>
			)}
		</Box>
	)
}

export default function CalendarTimeGrid({
	mode,               // 'days' | 'lanes'
	merchant = null,    // open hours + closed dates drive the hatched shading
	days = [],          // dayjs[] (days mode)
	date = null,        // dayjs (lanes mode)
	employees = [],     // lanes mode display employees
	getVisits,          // (day: dayjs, employeeId: string|null) => visit[]
	now,                // dayjs in merchant tz
	onSlotClick,        // (day: dayjs, mins: number, employeeId: string|null) => void
	onVisitClick,       // (visit) => void
	onOpenDay,          // (day: dayjs) => void
	scrollRef,
}) {
	const [ghost, setGhost] = useState({ key: null, mins: null })
	const bodyRef = useRef(null)
	const [colW, setColW] = useState(180)

	const nCols = mode === 'lanes' ? Math.max(1, employees.length) : Math.max(1, days.length)

	useLayoutEffect(() => {
		const el = bodyRef.current
		if (!el) return
		const measure = () => setColW(Math.max(40, (el.clientWidth - GUTTER_W) / nCols))
		measure()
		const ro = new ResizeObserver(measure)
		ro.observe(el)
		return () => ro.disconnect()
	}, [nCols])

	const hoverSlot = useCallback((key, mins) => {
		setGhost(mins == null ? { key: null, mins: null } : { key, mins })
	}, [])

	const gridCols = `${GUTTER_W}px repeat(${nCols}, 1fr)`
	const isTodayVisible = mode === 'lanes'
		? date && date.isSame(now, 'day')
		: days.some(d => d.isSame(now, 'day'))

	const renderColumn = ({ key, day, employeeId, className, isToday, isWeekend, side }) => {
		const visits = getVisits(day, employeeId)
		const placed = placeClean(visits, colW)
		return (
			<Box
				key={key}
				onMouseMove={(e) => hoverSlot(key, slotFromEvent(e, e.currentTarget))}
				onMouseLeave={() => hoverSlot(null, null)}
				onClick={(e) => {
					const mins = slotFromEvent(e, e.currentTarget)
					if (mins != null) onSlotClick(day, mins, employeeId)
				}}
				sx={{
					...colBgSx,
					cursor: 'pointer',
					...(isWeekend ? { bgcolor: '#FBFAFD' } : {}),
					...(isToday ? { bgcolor: 'rgba(124, 107, 217, 0.04)' } : {}),
				}}
				className={className}
			>
				<OffHoursShading day={day} merchant={merchant} />
				{ghost.key === key && ghost.mins != null && <Ghost mins={ghost.mins} />}
				{placed.map((p, j) => p.kind === 'appt' ? (
					<CalApptCard
						key={p.visit.key}
						visit={p.visit}
						now={now}
						side={side}
						style={{ ...p.style, zIndex: p.z }}
						onClick={(e) => { e.stopPropagation(); onVisitClick(p.visit) }}
					/>
				) : (
					<CalClusterCard
						key={`cl${j}`}
						items={p.items}
						start={p.start}
						end={p.end}
						side={side}
						style={{ ...p.style, zIndex: p.z }}
						onOpen={(e) => { e.stopPropagation(); onOpenDay(day) }}
					/>
				))}
				{isToday && <NowLine now={now} />}
			</Box>
		)
	}

	return (
		<Box sx={{
			bgcolor: '#FFFFFF', border: `1px solid ${BORDER}`, borderRadius: '14px',
			boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)', overflow: 'hidden',
			display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, fontFamily: FONT,
		}}>
			<Box ref={scrollRef} data-calendar-scroll sx={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollBehavior: 'smooth' }}>
				{/* sticky header */}
				<Box sx={{
					display: 'grid', gridTemplateColumns: gridCols, borderBottom: `1.5px solid ${BORDER}`,
					position: 'sticky', top: 0, zIndex: 50, bgcolor: '#FFFFFF',
					'& > * + *': { borderLeft: `1px solid ${COL_LINE}` },
				}}>
					<div />
					{mode === 'lanes'
						? employees.map(emp => {
							const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim()
							const count = getVisits(date, emp.id).length
							return (
								<Box key={emp.id} sx={{ display: 'flex', alignItems: 'center', gap: '9px', p: '9px 12px', minWidth: 0 }}>
									<Box sx={{
										width: 34, height: 34, flex: 'none', borderRadius: '50%',
										display: 'flex', alignItems: 'center', justifyContent: 'center',
										fontSize: '12px', fontWeight: 800, bgcolor: '#F0EEF6', color: '#5C5578',
									}}>
										{calInitials(name)}
									</Box>
									<Box component="strong" sx={{
										fontSize: '13px', fontWeight: 750, letterSpacing: '-0.01em',
										whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
										color: TEXT, minWidth: 0,
									}}>
										{name}
									</Box>
									{count > 0 && (
										<Box component="span" sx={{
											ml: 'auto', flex: 'none', minWidth: 22, height: 22, px: '7px',
											borderRadius: '99px', display: 'flex', alignItems: 'center',
											justifyContent: 'center', fontSize: '11px', fontWeight: 800,
											bgcolor: '#EDE9FB', color: '#5F4EC2',
										}}>
											{count}
										</Box>
									)}
								</Box>
							)
						})
						: days.map(d => {
							const isToday = d.isSame(now, 'day')
							return (
								<Box key={d.format('YYYY-MM-DD')} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', p: '10px 0 9px' }}>
									<Box component="span" sx={{
										fontSize: '11px', fontWeight: 750, letterSpacing: '0.06em',
										textTransform: 'uppercase', color: isToday ? '#5F4EC2' : MUTED,
									}}>
										{d.format('ddd')}
									</Box>
									<Box component="span" sx={{
										fontSize: '14.5px', fontWeight: 800, color: TEXT, width: 27, height: 27,
										display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
										...(isToday ? {
											bgcolor: '#7C6BD9', color: '#FFFFFF',
											boxShadow: '0 2px 6px rgba(124, 107, 217, 0.4)',
										} : {}),
									}}>
										{d.date()}
									</Box>
								</Box>
							)
						})}
				</Box>

				{/* body */}
				<Box ref={bodyRef} sx={{ display: 'grid', gridTemplateColumns: gridCols, position: 'relative', height: GRID_H }}>
					<Gutter now={now} showNow={isTodayVisible} />
					{mode === 'lanes'
						? employees.map((emp, i) => renderColumn({
							key: emp.id,
							day: date,
							employeeId: emp.id,
							isToday: date.isSame(now, 'day'),
							isWeekend: false,
							side: employees.length > 1 && i >= employees.length - 2 ? 'left' : 'right',
						}))
						: days.map((d, i) => renderColumn({
							key: d.format('YYYY-MM-DD'),
							day: d,
							employeeId: null,
							isToday: d.isSame(now, 'day'),
							isWeekend: d.day() === 0 || d.day() === 6,
							side: days.length > 1 && i >= days.length - 2 ? 'left' : 'right',
						}))}
				</Box>
			</Box>
		</Box>
	)
}
